import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import AssignUserModal from "./AssignUserModal";
import { useAuthStore } from "../store/useAuthStore";
import { api } from "../api";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { getSocket } from "../socket";

/** ===== Deploy-friendly perf knobs ===== */
const TASK_COUNTS_MODE = "topN"; // "topN" | "selected" | "all"
const TOPN_COUNT = 12; // topN projects only to avoid N*2 calls
const COUNTS_BATCH_SIZE = 6; // parallel API window (avoid flood)
const MAX_COUNTS_PROJECTS = 100; // optional hard cap to avoid storms

export default function ProjectsSidebar({
  projects = [],
  loading,
  selectedProject,
  setSelectedProject,
  refreshProjects,
  isMobile = false,
  onVisibleProjectsChange = function () {},
  onAfterAssign = function () {},
  onBack,
  /** Auto-open support (highest priority after URL/state) */
  initialOpenProjectId,
  initialOpenProjectName,
}) {
  /* ---------------- state ---------------- */
  const [searchTerm, setSearchTerm] = useState("");
  const [debounced, setDebounced] = useState("");

  const [assignModalProject, setAssignModalProject] = useState(null);
  const [users] = useState([]); // prop-shape only
  const [drawingMaster] = useState([]); // prop-shape only

  const [projectTaskCounts, setProjectTaskCounts] = useState({});
  const [employeeWorkedProjectNames, setEmployeeWorkedProjectNames] = useState(
    [],
  );

  // meta: { [projectId]: { unread, lastText, lastSender, lastAt } }
  const [msgMeta, setMsgMeta] = useState({});

  const [showSpinner, setShowSpinner] = useState(false);
  const didAutoSelectRef = useRef(false); // prevent repeated auto-selects

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    let t;
    if (loading)
      t = setTimeout(() => {
        setShowSpinner(true);
      }, 300);
    else setShowSpinner(false);
    return () => {
      if (t) clearTimeout(t);
    };
  }, [loading]);

  // debounce search (keep UI snappy on deploy)
  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(searchTerm.trim().toLowerCase());
    }, 250);
    return () => clearTimeout(id);
  }, [searchTerm]);

  const { authUser } = useAuthStore();
  const role = String((authUser && authUser.role) || "").toLowerCase();
  const isAdmin = role === "admin";
  const isClient = role === "client";
  const isEmployee = !isAdmin && !isClient;
  const canAssign = isAdmin || isEmployee;

  // Harden header so we never send "Bearer null"
  const token = localStorage.getItem("token") || "";
  const authHeader = token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : { headers: {} };

  const getProjName = (p) =>
    String(p.projectName || p.name || "Untitled Project").trim();

  /* ---------------- visible projects ---------------- */
  useEffect(() => {
    if (!isEmployee || !(authUser && authUser._id)) return;
    api
      .get("/logs/my", authHeader)
      .then((res) => {
        const logs = Array.isArray(res.data) ? res.data : [];
        const names = logs
          .map((l) => String(l.projectName || "").toLowerCase())
          .filter(Boolean);
        const unique = Array.from(new Set(names));
        setEmployeeWorkedProjectNames(unique);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser && authUser._id, isEmployee]);

  const visibleProjects = useMemo(() => {
    if (isAdmin) return projects;

    const myId = authUser && authUser._id ? authUser._id : "";
    const isAssignedToMe = (p) => {
      const list = Array.isArray(p.assignedUsers) ? p.assignedUsers : [];
      for (let i = 0; i < list.length; i++) {
        const u = list[i];
        if (typeof u === "string") {
          if (u === myId) return true;
        } else if (u && String(u._id) === String(myId)) {
          return true;
        }
      }
      return false;
    };

    if (isClient) return projects.filter(isAssignedToMe);

    const workedSet = new Set(employeeWorkedProjectNames);
    const assigned = projects.filter(isAssignedToMe);
    const worked = projects.filter((p) =>
      workedSet.has(getProjName(p).toLowerCase()),
    );

    const map = new Map();
    assigned.forEach((p) => map.set(String(p._id), p));
    worked.forEach((p) => map.set(String(p._id), p));
    return Array.from(map.values());
  }, [
    projects,
    authUser && authUser._id,
    isAdmin,
    isClient,
    isEmployee,
    employeeWorkedProjectNames,
  ]);

  useEffect(() => {
    onVisibleProjectsChange(visibleProjects);
  }, [onVisibleProjectsChange, visibleProjects]);

  /* ---------------- task counts (deploy-friendly) ---------------- */
  useEffect(() => {
    let cancelled = false;

    async function fetchCountsDeployFriendly(list) {
      // Decide which projects to fetch counts for
      let targets = [];
      if (TASK_COUNTS_MODE === "selected" && selectedProject) {
        targets = [selectedProject];
      } else if (TASK_COUNTS_MODE === "topN") {
        targets = list.slice(0, TOPN_COUNT);
      } else {
        targets = list;
      }

      if (!targets.length) {
        setProjectTaskCounts({});
        return;
      }

      // Optional: skip counts if too many visible projects (avoid storms)
      if (list.length > MAX_COUNTS_PROJECTS) {
        setProjectTaskCounts({});
        return;
      }

      const headers = authHeader.headers;
      const results = {};
      const chunks = [];
      for (let i = 0; i < targets.length; i += COUNTS_BATCH_SIZE) {
        chunks.push(targets.slice(i, i + COUNTS_BATCH_SIZE));
      }
      for (const batch of chunks) {
        const batchRes = await Promise.all(
          batch.map(async (p) => {
            try {
              const [pend, comp] = await Promise.all([
                api.get("/tasks/project/" + p._id + "?status=pending", {
                  headers,
                }),
                api.get("/tasks/project/" + p._id + "?status=completed", {
                  headers,
                }),
              ]);
              return [
                p._id,
                {
                  pending: Array.isArray(pend.data) ? pend.data.length : 0,
                  completed: Array.isArray(comp.data) ? comp.data.length : 0,
                },
              ];
            } catch {
              return [p._id, { pending: 0, completed: 0 }];
            }
          }),
        );
        batchRes.forEach(([id, obj]) => (results[id] = obj));
        // yield to UI
        await new Promise((r) => setTimeout(r, 0));
      }
      if (cancelled) return;

      // single state update (important for deploy perf)
      setProjectTaskCounts((prev) => ({ ...prev, ...results }));
    }

    fetchCountsDeployFriendly(visibleProjects);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleProjects, selectedProject && selectedProject._id]);

  /* ---------------- unread / last message helpers ---------------- */
  const formatTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const t = new Date();
      const sameDay =
        d.getFullYear() === t.getFullYear() &&
        d.getMonth() === t.getMonth() &&
        d.getDate() === t.getDate();
      return sameDay
        ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        : d.toLocaleDateString("en-GB");
    } catch (e) {
      return "";
    }
  };

  const previewFromMessage = (m = {}) => {
    const s = m.sender || {};
    const sender = s.name || s.username || s.email || m.senderLabel || "";
    if (m.fileName || m.file) {
      const seg = String(m.file || "").split("/");
      const fname = m.fileName || (seg.length ? seg[seg.length - 1] : "file");
      return { lastSender: sender, lastText: "📎 " + fname };
    }
    const text = String(m.content || m.text || "")
      .replace(/\s+/g, " ")
      .trim();
    return { lastSender: sender, lastText: text.slice(0, 60) };
  };

  const hydrateMetaBatch = useCallback(async (ids) => {
    if (!ids.length) return {};
    const headers = authHeader.headers;

    try {
      // ✅ FIX 1: POST instead of GET
      const res = await api.post(
        "/project-messages/summary",
        { projectIds: ids },
        { headers, timeout: 15000 },
      );

      const arr = Array.isArray(res.data) ? res.data : [];
      const out = {};

      arr.forEach((row) => {
        const projectId = row.projectId;
        const unread = Number(row.unread || 0);
        const lastMessage = row.lastMessage || null;
        const lastAt =
          row.lastAt || (lastMessage && lastMessage.createdAt) || null;

        const pv = previewFromMessage(lastMessage || {});
        out[projectId] = {
          unread,
          lastText: pv.lastText || "",
          lastSender: pv.lastSender || "",
          lastAt,
        };
      });

      return out;
    } catch (e) {
      // ✅ FIX 2: fallback only unread-count (NO /last API)
      const results = await Promise.all(
        ids.map(async (id) => {
          try {
            const cRes = await api.get(
              "/project-messages/" + id + "/unread-count",
              { headers, timeout: 12000 },
            );

            return [
              id,
              {
                unread: Number(cRes?.data?.unread || 0),
                lastText: "",
                lastSender: "",
                lastAt: null,
              },
            ];
          } catch {
            return [
              id,
              { unread: 0, lastText: "", lastSender: "", lastAt: null },
            ];
          }
        }),
      );

      const out = {};
      results.forEach(([id, meta]) => {
        out[id] = meta;
      });
      return out;
    }
  }, []);
  // safe

  const refreshMsgMeta = useCallback(async () => {
    const ids = visibleProjects.map((p) => p._id);
    if (!ids.length) {
      setMsgMeta({});
      return;
    }
    const incoming = await hydrateMetaBatch(ids);
    setMsgMeta((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        const inc = incoming[id] || {};
        const cur = prev[id] || {
          unread: 0,
          lastText: "",
          lastSender: "",
          lastAt: null,
        };
        const safeLastAt = inc.lastAt ? inc.lastAt : cur.lastAt || null;
        next[id] = {
          unread: typeof inc.unread === "number" ? inc.unread : cur.unread,
          lastText: inc.lastText != null ? inc.lastText : cur.lastText,
          lastSender: inc.lastSender != null ? inc.lastSender : cur.lastSender,
          lastAt: safeLastAt,
        };
      });
      return next;
    });
  }, [visibleProjects, hydrateMetaBatch]);

  useEffect(() => {
    refreshMsgMeta();
  }, [refreshMsgMeta, JSON.stringify(visibleProjects)]); // eslint-disable-line

  // periodic / focus refresh
  useEffect(() => {
    const onFocus = () => {
      refreshMsgMeta();
    };
    window.addEventListener("focus", onFocus);
    const t = setInterval(refreshMsgMeta, 20000);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(t);
    };
  }, [refreshMsgMeta]);

  // mark selected project read (do NOT touch lastAt)
  useEffect(() => {
    if (!(selectedProject && selectedProject._id)) return;
    const pid = selectedProject._id;
    const doMark = async () => {
      try {
        await api.post("/project-messages/" + pid + "/read", {}, authHeader);
      } catch {}
      setMsgMeta((prev) => {
        const cur = prev[pid] || {};
        const next = { ...prev };
        next[pid] = { ...cur, unread: 0 };
        return next;
      });
    };
    doMark();
  }, [selectedProject && selectedProject._id]); // eslint-disable-line

  // realtime: move project to top on new message
  useEffect(() => {
    const socket = getSocket?.();
    if (!socket) return;

    const onIncoming = (msg) => {
      msg = msg || {};
      const pid = String(msg.project || msg.projectId || "");
      if (!pid) return;

      const pv = previewFromMessage(msg);
      setMsgMeta((prev) => {
        const cur = prev[pid] || {
          unread: 0,
          lastText: "",
          lastSender: "",
          lastAt: null,
        };
        const opened = selectedProject && String(selectedProject._id) === pid;
        const next = { ...prev };
        next[pid] = {
          unread: opened ? 0 : (cur.unread || 0) + 1,
          lastText: pv.lastText,
          lastSender: pv.lastSender,
          lastAt: msg.createdAt || new Date().toISOString(),
        };
        return next;
      });
      const senderId =
        typeof msg.sender === "object" ? msg.sender?._id : msg.sender;

      if (senderId !== authUser?._id && Notification.permission === "granted") {
        new Notification("New Project Message", {
          body:
            (msg.sender?.name || "Someone") +
            ": " +
            (msg.content || "📎 File received"),
        });
      }
    };

    socket.on?.("project:message", onIncoming);
    return () => {
      socket.off?.("project:message", onIncoming);
    };
  }, [selectedProject?._id, authUser?._id]); // eslint-disable-line

  // realtime: refresh projects list on membership changes
  useEffect(() => {
    const socket = getSocket?.();
    if (!socket || !refreshProjects) return;

    const onAdded = () => refreshProjects();
    const onRemoved = () => refreshProjects();

    socket.on?.("project:added", onAdded);
    socket.on?.("project:removed", onRemoved);
    return () => {
      socket.off?.("project:added", onAdded);
      socket.off?.("project:removed", onRemoved);
    };
  }, [refreshProjects]);

  /* ---------------- after assign ---------------- */
  const handleAfterAssign = async () => {
    if (refreshProjects) await refreshProjects();
    onAfterAssign();
    setTimeout(refreshMsgMeta, 0);
  };

  /* ---------------- back nav ---------------- */
  const navigate = useNavigate();
  const handleBackClick = () => {
    if (typeof onBack === "function") onBack();
    else navigate("/admin");
  };

  /* ---------------- Improved auto-select precedence ----------------
     1) URL/state (/?project=...) or location.state.openProjectName
     2) initialOpenProjectId / initialOpenProjectName (prop)
     3) lastProjectId (localStorage) — *only if nothing else present*
  ------------------------------------------------------------------*/
  useEffect(() => {
    if (didAutoSelectRef.current) return;
    if (!projects || projects.length === 0) return;
    if (selectedProject) return;

    const stateName =
      (location.state && location.state.openProjectName) || null;
    const queryName = searchParams.get("project");

    let found = null;

    // 1) URL/state
    const wantName = (stateName || queryName || "").trim().toLowerCase();
    if (wantName) {
      const exact = projects.find(
        (p) => getProjName(p).toLowerCase() === wantName,
      );
      found =
        exact ||
        projects.find((p) => getProjName(p).toLowerCase().includes(wantName));
    }

    // 2) Props
    if (!found && initialOpenProjectId) {
      found = projects.find(
        (p) => String(p._id) === String(initialOpenProjectId),
      );
    }
    if (!found && initialOpenProjectName) {
      const target = String(initialOpenProjectName).trim().toLowerCase();
      const exact = projects.find(
        (p) => getProjName(p).toLowerCase() === target,
      );
      found =
        exact ||
        projects.find((p) => getProjName(p).toLowerCase().includes(target));
    }

    // 3) lastProjectId (fallback only)
    // 3) lastProjectId (ONLY DESKTOP)
    if (!found && !isMobile) {
      const last = localStorage.getItem("lastProjectId");
      if (last) {
        found = projects.find((p) => String(p._id) === String(last));
      }
    }

    if (found) {
      setSelectedProject(found);
      try {
        localStorage.setItem("lastProjectId", String(found._id));
      } catch {}
      // normalize URL param so refresh keeps correct project
      setSearchParams(
        (sp) => {
          const next = new URLSearchParams(sp);
          next.set("project", getProjName(found));
          return next;
        },
        { replace: true },
      );
    } else {
      // clear stale lastProjectId if it doesn't exist anymore
      localStorage.removeItem("lastProjectId");
    }

    didAutoSelectRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projects,
    selectedProject,
    initialOpenProjectId,
    initialOpenProjectName,
    location.key,
    isMobile,
  ]);

  /* ---------------- UI styles ---------------- */
  const shell = {
    width: "100%",
    minHeight: "100vh",
    maxHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#f9f9f9",
    overflowY: "auto",
  };

  const backBarH = isMobile ? 42 : 44;
  const headerH = isMobile ? 44 : 48;
  const searchTop = backBarH + headerH;

  const backBar = {
    position: "sticky",
    top: 0,
    zIndex: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: isMobile ? "8px 10px" : "10px 12px",
    background: "#fff",
    borderBottom: "1px solid #e7e7e7",
  };
  const backBtn = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    border: "1px solid #dcdcdc",
    background: "#f9fafb",
    padding: isMobile ? "6px 10px" : "8px 12px",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: isMobile ? 13 : 14,
    transition: "background .12s ease, border-color .12s ease",
  };

  const header = {
    position: "sticky",
    top: backBarH,
    zIndex: 2,
    background: "#f9f9f9",
    padding: isMobile ? "10px 12px 8px" : "12px 14px 8px",
    borderBottom: "1px solid #e7e7e7",
  };

  const searchWrap = {
    position: "sticky",
    top: searchTop,
    zIndex: 3,
    background: "#f9f9f9",
    padding: "10px 12px",
    borderBottom: "1px solid #eee",
  };
  const searchBox = { position: "relative", width: "85%" };
  const searchInput = {
    width: "100%",
    padding: "10px 38px",
    border: "1px solid #d1d5db",
    borderRadius: 999,
    outline: "none",
    fontSize: 14,
    background: "#fff",
    transition: "box-shadow 120ms, border-color 120ms",
  };
  const searchIcon = {
    position: "absolute",
    left: 14,
    top: "50%",
    transform: "translateY(-50%)",
    width: 18,
    height: 18,
    pointerEvents: "none",
    color: "#6b7280",
  };
  const clearBtn = {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    width: 26,
    height: 26,
    borderRadius: 13,
    border: "1px solid #e5e7eb",
    background: "#fff",
    cursor: "pointer",
    display: searchTerm ? "flex" : "none",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
  };

  const list = { padding: 10 };

  const row = (active) => ({
    display: "grid",
    gridTemplateColumns: "40px 1fr auto",
    gap: 10,
    alignItems: "center",
    padding: 10,
    border: "1px solid #a9a9a9",
    borderRadius: 8,
    background: active ? "#e7f1ff" : "#fff",
    cursor: "pointer",
    marginBottom: 10,
  });

  const avatar = {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "#e5e7eb",
    display: "grid",
    placeItems: "center",
    fontSize: 16,
    color: "#374151",
  };
  const centerCol = { minWidth: 0 };

  const nameRow = {
    display: "flex",
    gap: 8,
    alignItems: "center",
    minWidth: 0,
  };

  const projNameStyle = {
    fontWeight: 700,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    lineHeight: 1.2,
    maxHeight: "2.4em",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  };

  const preview = {
    marginTop: 2,
    fontSize: 12,
    color: "#6b7280",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const timeStyle = {
    fontSize: 12,
    color: "#374151",
    whiteSpace: "nowrap",
    alignSelf: "start",
  };

  const badge = {
    background: "#16a34a",
    color: "#fff",
    fontSize: 11,
    fontWeight: 800,
    minWidth: 24,
    height: 24,
    padding: "0 6px",
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 6,
    flex: "0 0 auto",
  };

  const meta = { fontSize: 12, color: "#666" };
  const assignBtn = {
    marginTop: 8,
    width: "100%",
    fontSize: 12,
    padding: "7px",
    border: "0.5px solid #0f8a0f",
    borderRadius: 6,
    backgroundColor: "#e6e6e6",
    color: "#707171",
    cursor: "pointer",
  };

  const backHover = (e) => {
    e.currentTarget.style.background = "#eef2ff";
    e.currentTarget.style.borderColor = "#c7d2fe";
  };
  const backLeave = (e) => {
    e.currentTarget.style.background = "#f9fafb";
    e.currentTarget.style.borderColor = "#dcdcdc";
  };

  /* ---------------- ordering: newest first ---------------- */
  const orderedVisible = useMemo(() => {
    const withTimes = visibleProjects.map((p) => {
      const metaRow = msgMeta[p._id] || {};
      const at = metaRow.lastAt ? Date.parse(metaRow.lastAt) : 0;
      return { p, at: isNaN(at) ? 0 : at };
    });
    withTimes.sort((a, b) => {
      if (b.at !== a.at) return b.at - a.at; // newest first
      return getProjName(a.p).localeCompare(getProjName(b.p));
    });
    return withTimes.map((x) => x.p);
  }, [visibleProjects, msgMeta]);

  // search AFTER ordering
  const filteredProjects = useMemo(() => {
    if (!debounced) return orderedVisible;
    return orderedVisible.filter((p) =>
      getProjName(p).toLowerCase().includes(debounced),
    );
  }, [orderedVisible, debounced]);

  /* ---------------- render ---------------- */
  return (
    <div style={shell}>
      {/* Back Bar */}
      <div style={backBar}>
        <button
          type="button"
          onClick={handleBackClick}
          onMouseEnter={backHover}
          onMouseLeave={backLeave}
          style={backBtn}
          title="Back to Dashboard"
          aria-label="Back to Dashboard"
        >
          <ArrowLeft size={16} />
          <span>Back to Dashboard</span>
        </button>
        <div style={{ width: 12 }} />
      </div>

      {/* Header */}
      <div style={header}>
        <h4 style={{ margin: 0, textAlign: "center", fontWeight: 700 }}>
          PROJECTS COMMUNICATION
        </h4>
      </div>

      {/* Search */}
      <div style={searchWrap}>
        <div style={searchBox}>
          <svg viewBox="0 0 24 24" fill="none" style={searchIcon}>
            <path
              d="M21 21l-4.2-4.2M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <input
            type="text"
            placeholder="Search projects…"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            onFocus={(e) => {
              e.currentTarget.style.boxShadow = "0 0 0 3px rgba(63,131,248,.2)";
              e.currentTarget.style.borderColor = "#9ca3af";
            }}
            onBlur={(e) => {
              e.currentTarget.style.boxShadow = "none";
              e.currentTarget.style.borderColor = "#d1d5db";
            }}
            style={searchInput}
          />
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setSearchTerm("");
            }}
            style={clearBtn}
            title="Clear"
          >
            ×
          </button>
        </div>
      </div>

      {/* List */}
      <div style={list}>
        {showSpinner ? (
          <p style={{ padding: 8, color: "#666" }}>Loading...</p>
        ) : filteredProjects.length === 0 ? (
          <p style={{ padding: 8, color: "#666" }}>No projects found.</p>
        ) : (
          filteredProjects.map((proj) => {
            const active = selectedProject && selectedProject._id === proj._id;
            const displayName = getProjName(proj);
            const counts = projectTaskCounts[proj._id] || {
              pending: 0,
              completed: 0,
            };
            const metaRow = msgMeta[proj._id] || {
              unread: 0,
              lastText: "",
              lastSender: "",
              lastAt: null,
            };

            // Prefer `members` (new route), fallback to `assignedUsers`
            const membersCount = Array.isArray(proj.members)
              ? proj.members.length
              : (proj.assignedUsers && proj.assignedUsers.length) || 0;

            return (
              <div
                key={proj._id}
                onClick={() => {
                  setSelectedProject(proj);
                  try {
                    localStorage.setItem("lastProjectId", String(proj._id));
                  } catch {}
                  // reflect selection in URL so next visit doesn't stick wrong one
                  setSearchParams(
                    (sp) => {
                      const next = new URLSearchParams(sp);
                      next.set("project", displayName);
                      return next;
                    },
                    { replace: true },
                  );
                  // instant local unread clear
                  setMsgMeta((prev) => ({
                    ...prev,
                    [proj._id]: { ...(prev[proj._id] || {}), unread: 0 },
                  }));
                }}
                style={row(active)}
              >
                {/* avatar */}
                <div style={avatar}>👥</div>

                {/* center */}
                <div style={centerCol}>
                  <div style={nameRow}>
                    <span style={projNameStyle} title={displayName}>
                      {displayName}
                    </span>
                    {metaRow.unread > 0 ? (
                      <span style={badge}>{metaRow.unread}</span>
                    ) : null}
                  </div>

                  <div style={preview}>
                    {metaRow.lastSender
                      ? metaRow.lastSender + ": " + (metaRow.lastText || "")
                      : ""}
                  </div>

                  <div style={meta}>
                    Members: {membersCount} • Tasks: {counts.pending} pending ·{" "}
                    {counts.completed} done
                  </div>

                  {canAssign ? (
                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <button
                        style={assignBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAssignModalProject(proj);
                        }}
                      >
                        Assign User
                      </button>
                    </div>
                  ) : null}
                </div>

                {/* time */}
                <div style={timeStyle}>
                  {metaRow.lastAt ? formatTime(metaRow.lastAt) : ""}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal */}
      {assignModalProject ? (
        <AssignUserModal
          project={assignModalProject}
          onClose={() => {
            setAssignModalProject(null);
          }}
          onAssignSuccess={handleAfterAssign}
          users={users}
          drawingMaster={drawingMaster}
        />
      ) : null}
    </div>
  );
}
